import { useFetch } from "@/lib/axios.config.ts";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ImageType } from "@/pages/challenges/types/common";

export interface Partner {
  id: string;
  name: string;
  icon: ImageType | null;
  description: string;
  // don_per_visit: number; // deprecated — DON is earned per product purchase, not per visit
}

export type VoucherStatus =
  | "AVAILABLE"
  | "REDEEMED"
  | "USED"
  | "EXPIRED"
  | "SOLD_OUT";

export interface Voucher {
  id: string;
  title: string;
  description: string;
  don_cost: number;
  is_hot: boolean;
  status: VoucherStatus;
  icon: ImageType | null;
  images: ImageType[];
  promo_code: string | null;
  details: string | null;
  how_to_use: string | null;
  link: string | null;
  end_date: string | null;
  total_quantity: number | null;
  remaining_quantity: number | null;
  redeemed_at: string | null;
  valid_days_after_redeem: number | null;
  redeem_valid_until: string | null;
}

export interface VoucherRedeemResponse {
  success: boolean;
  message: string;
}

export interface GenerateQRResponse {
  qr_data: string;
  expires_in: number;
}

export interface MyPartner {
  id: string;
  name: string;
  description: string;
  icon: ImageType | null;
  // don_per_visit: number; // deprecated — DON is earned per product purchase, not per visit
  total_don_earned: number;
  visit_count: number;
  last_visited_at: string | null;
}

export interface Product {
  id: string;
  title: string;
  description: string;
  details: string | null;
  icon: ImageType | null;
  don_price: number;
  don_reward: number;
  is_hot: boolean;
  status: string;
  link: string | null;
  end_date: string | null;
  total_quantity: number | null;
  remaining_quantity: number | null;
}

export interface PartnerDetail {
  id: string;
  name: string;
  description: string;
  icon: ImageType | null;
  // don_per_visit: number; // deprecated — DON is earned per product purchase, not per visit
  products: Product[];
}

const useGetPartners = () => {
  return useQuery({
    queryKey: ["loyaltyPartners"],
    queryFn: async () => {
      const res = await useFetch.get<Partner[]>("loyalty/LoyaltyPartners");
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

const useGetVouchers = () => {
  return useQuery({
    queryKey: ["vouchers"],
    queryFn: async () => {
      const res = await useFetch.get<Voucher[]>("loyalty/Vouchers");
      return res.data;
    },
    staleTime: 60 * 1000,
  });
};

const useRedeemVoucher = () => {
  return useMutation({
    mutationKey: ["redeemVoucher"],
    mutationFn: async (voucherId: string) => {
      const res = await useFetch.post<VoucherRedeemResponse>(
        "loyalty/RedeemVoucher",
        { voucher_id: voucherId }
      );
      return res.data;
    },
  });
};

const useGetMyPartners = () => {
  return useQuery({
    queryKey: ["myPartners"],
    queryFn: async () => {
      const res = await useFetch.get<MyPartner[]>("loyalty/MyPartners");
      return res.data;
    },
    staleTime: 60 * 1000,
  });
};

const useGetPartnerDetail = (partnerId: string | undefined) => ({
  queryKey: ["loyaltyPartnerDetail", partnerId] as const,
  queryFn: async () => {
    const res = await useFetch.get<PartnerDetail>(
      `loyalty/PartnerDetail/${partnerId}`
    );
    return res.data;
  },
  staleTime: 60 * 1000,
  enabled: Boolean(partnerId),
});

const useGenerateQR = () => {
  return useMutation({
    mutationKey: ["generateLoyaltyQR"],
    mutationFn: async () => {
      const res = await useFetch.post<GenerateQRResponse>(
        "loyalty/GenerateQR"
      );
      return res.data;
    },
  });
};

export interface PartnerFAQ {
  id: string;
  question: string;
  answer: string;
  order: number;
}

const useGetPartnerFAQs = () => {
  return useQuery({
    queryKey: ["partnerFAQs"],
    queryFn: async () => {
      const res = await useFetch.get<PartnerFAQ[]>("loyalty/PartnerFAQs");
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export { useGetPartners, useGetMyPartners, useGetVouchers, useRedeemVoucher, useGenerateQR, useGetPartnerDetail, useGetPartnerFAQs };
